import { useMemo } from 'react';

import { BrregCompanyData } from '../../../companies/hooks/useBrregCompanySearch';
import { ImportCompanyCandidate, OnboardingBuilding, OnboardingUser } from '../onboarding.types';
import { OnboardingActions } from '../onboarding-actions';
import { OnboardingProgress, ProgressStage } from '../onboarding-progress';

interface StepGjennomgangProps {
  company: BrregCompanyData | null;
  users: OnboardingUser[];
  buildings: OnboardingBuilding[];
  importCompanies: ImportCompanyCandidate[];
  onBack: () => void;
  onSubmit: () => void;
  isSubmitting: boolean;
  submitError: string | null;
}

export function StepGjennomgang({
  company,
  users,
  buildings,
  importCompanies,
  onBack,
  onSubmit,
  isSubmitting,
  submitError,
}: StepGjennomgangProps) {
  const selectedBuildings = useMemo(() => buildings.filter((b) => b.selected), [buildings]);

  const totalBra = useMemo(
    () => selectedBuildings.reduce((sum, b) => sum + b.bra, 0),
    [selectedBuildings],
  );

  const selectedImports = useMemo(
    () => importCompanies.filter((c) => c.selected && c.orgNumber !== company?.organisasjonsnummer),
    [importCompanies, company?.organisasjonsnummer],
  );

  const progressStages: ProgressStage[] = useMemo(
    () => [
      { key: 'company', label: 'Oppretter selskap og konto' },
      { key: 'buildings', label: 'Oppretter bygg og etasjer' },
      { key: 'package', label: 'Tildeler pakke' },
      {
        key: 'imports',
        label: `Henter og lagrer leietakere (${selectedImports.length})`,
        skip: selectedImports.length === 0,
      },
      {
        key: 'users',
        label: `Inviterer brukere (${users.length})`,
        skip: users.length === 0,
      },
      { key: 'finalize', label: 'Fullfører registreringen' },
    ],
    [selectedImports.length, users.length],
  );

  // During submit or after error we show the progress view instead of the summary.
  const showProgress = isSubmitting || !!submitError;

  return (
    <div>
      <h2 className="text-2xl font-bold text-white mb-2">Gjennomgang</h2>
      <p className="text-white/60 mb-8">
        {showProgress ? 'Vi oppretter kontoen din nå…' : 'Kontroller at alt ser riktig ut før du fullfører.'}
      </p>

      {showProgress ? (
        <OnboardingProgress
          stages={progressStages}
          isSubmitting={isSubmitting}
          isComplete={false}
          error={submitError}
        />
      ) : (
        <>
          {/* Company */}
          <section className="mb-6">
            <h3 className="text-xs font-semibold tracking-wider text-white/40 uppercase mb-3">Selskap</h3>
            <div className="p-4 rounded-lg border border-border bg-surface-base">
              {company ? (
                <>
                  <div className="text-white font-bold text-lg">{company.navn}</div>
                  <div className="text-white/50 text-sm mt-1">
                    Org.nr. {company.organisasjonsnummer}
                  </div>
                </>
              ) : (
                <div className="text-white/50">Ikke valgt</div>
              )}
            </div>
          </section>

          {/* Users */}
          <section className="mb-6">
            <h3 className="text-xs font-semibold tracking-wider text-white/40 uppercase mb-3">
              Brukere ({users.length})
            </h3>
            {users.length > 0 ? (
              <div className="space-y-2">
                {users.map((user) => (
                  <div
                    key={user.id}
                    className="flex items-center justify-between p-3 rounded-lg border border-border bg-surface-base"
                  >
                    <span className="text-white text-sm">{user.email}</span>
                    <span className="text-white/50 text-sm">{user.role}</span>
                  </div>
                ))}
              </div>
            ) : (
              <div className="p-4 rounded-lg border border-border bg-surface-base text-white/50 text-sm">
                Ingen brukere lagt til ennå.
              </div>
            )}
          </section>

          {/* Buildings */}
          <section className="mb-6">
            <h3 className="text-xs font-semibold tracking-wider text-white/40 uppercase mb-3">
              Bygg ({selectedBuildings.length}) · {totalBra.toLocaleString('nb-NO')} m²
            </h3>
            {selectedBuildings.length > 0 ? (
              <div className="space-y-2">
                {selectedBuildings.map((building) => (
                  <div
                    key={building.id}
                    className="flex items-center justify-between p-3 rounded-lg border border-border bg-surface-base"
                  >
                    <div>
                      <span className="text-white text-sm font-medium">{building.address}</span>
                      <span className="text-white/40 text-sm ml-2">
                        {building.postalCode} {building.city}
                      </span>
                    </div>
                    <div className="flex items-center gap-4">
                      <span className="text-white/50 text-sm">
                        {building.bra.toLocaleString('nb-NO')} m²
                      </span>
                      <span className="text-sm font-medium text-brand-primary">{building.role}</span>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="p-4 rounded-lg border border-border bg-surface-base text-white/50 text-sm">
                Ingen bygg valgt.
              </div>
            )}
          </section>

          {/* Import companies */}
          <section className="mb-6">
            <h3 className="text-xs font-semibold tracking-wider text-white/40 uppercase mb-3">
              Leietakere ({selectedImports.length})
            </h3>
            {selectedImports.length > 0 ? (
              <div className="p-4 rounded-lg border border-border bg-surface-base">
                <div className="text-white text-sm">
                  {selectedImports.length} {selectedImports.length === 1 ? 'selskap' : 'selskaper'} vil bli
                  opprettet som mulige leietakere i systemet.
                </div>
                <div className="text-white/40 text-xs mt-1">
                  Selskaper som allerede finnes knyttes til din konto uten duplisering.
                </div>
              </div>
            ) : (
              <div className="p-4 rounded-lg border border-border bg-surface-base text-white/50 text-sm">
                Ingen leietakere valgt for import.
              </div>
            )}
          </section>
        </>
      )}

      {!showProgress && (
        <OnboardingActions
          onBack={onBack}
          onNext={onSubmit}
          nextLabel="Fullfør →"
          nextDisabled={isSubmitting}
        />
      )}

      {showProgress && submitError && (
        <OnboardingActions
          onBack={onBack}
          onNext={onSubmit}
          nextLabel="Prøv igjen"
        />
      )}
    </div>
  );
}
